import { maskDocument } from 'utils/formatters';

/** Beneficiário do boleto como o backend devolve (consulta mais recente do boleto). */
export interface BilletBeneficiary {
  name: string | null;
  document: string | null;
  finalName: string | null;
  finalDocument: string | null;
}

type Party = {
  name?: string | null;
  document?: string | null;
  ispbName?: string | null;
};

/** Só os campos do saque que dizem quem recebeu — serve para a linha da listagem e para o detalhe. */
export interface RecipientSource {
  type?: string;
  receiverName?: string | null;
  receiverDocument?: string | null;
  receiverBankName?: string | null;
  billetBeneficiary?: BilletBeneficiary | null;
  CustomerAccounts?: { accountHolder?: string | null; accountHolderDocument?: string | null } | null;
  receiver?: { name?: string | null; document_number?: string | null } | null;
  withdrawSulcredi?: { creditorAccount?: Party | null } | null;
  WithdrawelSulcredi?: Array<{ status?: number; payeeName?: string | null; payeeDocument?: string | null }> | null;
}

export interface WithdrawalRecipient {
  /** "Beneficiário" no boleto, "Favorecido" no Pix. */
  label: string;
  name: string | null;
  document: string | null;
  bank: string | null;
}

const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value.trim() : null);

const first = (...values: unknown[]): string | null => {
  for (const value of values) {
    const found = text(value);
    if (found) return found;
  }
  return null;
};

const sameDocument = (a: string | null, b: string | null) =>
  (a ?? '').replace(/[^0-9a-z]/gi, '').toUpperCase() === (b ?? '').replace(/[^0-9a-z]/gi, '').toUpperCase();

/**
 * Quem recebeu o saque, do dado mais confiável para o mais genérico:
 * comprovante do banco (pago) → foto gravada no saque → tentativa paga → conta salva.
 *
 * A conta salva vem por último porque é compartilhada entre os saques da mesma
 * chave e tem o titular sobrescrito a cada pagamento. No boleto ela nem entra:
 * o beneficiário sai da consulta do boleto (o final, quando houver).
 */
export const getWithdrawalRecipient = (withdrawal: RecipientSource): WithdrawalRecipient => {
  const creditor = withdrawal.withdrawSulcredi?.creditorAccount;
  const paid = withdrawal.WithdrawelSulcredi?.find((attempt) => attempt?.status === 2);

  if (withdrawal.type === 'BILLET') {
    const billet = withdrawal.billetBeneficiary;
    return {
      label: 'Beneficiário',
      name: first(creditor?.name, billet?.finalName, billet?.name, withdrawal.receiverName, paid?.payeeName),
      document: first(
        creditor?.document,
        billet?.finalDocument,
        billet?.document,
        withdrawal.receiverDocument,
        paid?.payeeDocument,
      ),
      bank: first(creditor?.ispbName),
    };
  }

  return {
    label: 'Favorecido',
    name: first(
      creditor?.name,
      withdrawal.receiver?.name,
      withdrawal.receiverName,
      paid?.payeeName,
      withdrawal.CustomerAccounts?.accountHolder,
    ),
    document: first(
      creditor?.document,
      withdrawal.receiver?.document_number,
      withdrawal.receiverDocument,
      paid?.payeeDocument,
      withdrawal.CustomerAccounts?.accountHolderDocument,
    ),
    bank: first(creditor?.ispbName, withdrawal.receiverBankName),
  };
};

/**
 * Beneficiário registrado do boleto e, só quando é outro, o beneficiário final
 * (boleto emitido por intermediador: o registrado é o intermediador).
 */
export const getBilletBeneficiaries = (withdrawal: RecipientSource) => {
  const billet = withdrawal.billetBeneficiary;
  const name = first(billet?.name, withdrawal.receiverName);
  const document = first(billet?.document, withdrawal.receiverDocument);
  const finalName = text(billet?.finalName);
  const finalDocument = text(billet?.finalDocument);

  if (!name) {
    return { name: finalName, document: finalDocument, final: null };
  }

  const differs =
    (!!finalName && finalName.toUpperCase() !== name.toUpperCase()) ||
    (!!finalDocument && !sameDocument(finalDocument, document));

  return { name, document, final: differs ? { name: finalName, document: finalDocument } : null };
};

/** Mesma regra do comprovante: documento mascarado pelo DICT (com *) sai como veio. */
export const formatRecipientDocument = (document?: string | null): string | null => {
  const value = text(document);
  if (!value) return null;
  return value.includes('*') ? value : maskDocument(value);
};
